import { useState, useEffect } from 'react';
import api from '../../services/api';
import useEscapeClose from '../../hooks/useEscapeClose';
import { BarChart3, X, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Modale réutilisable d'export PDF (attestation Qualiopi) des résultats partagés.
 * Utilisée par ShareCodesPanel (Gérer les partages) ET TabResultats (Suivi des résultats).
 *
 * Props :
 *  - open: bool — ouverture contrôlée par le parent
 *  - onClose: () => void
 *  - thematiqueId: number
 *  - codes, categoryCodes, quizCodes, userStats: données des codes/participants
 *  - initialSelectedIds?: Set|array — présélection (ex. export rapide d'un seul participant)
 */
export default function ResultsExportModal({
    open,
    onClose,
    thematiqueId,
    codes = [],
    categoryCodes = [],
    quizCodes = [],
    userStats = [],
    initialSelectedIds = null,
}) {
    const [exportOrg, setExportOrg] = useState('');
    const [exportTrainer, setExportTrainer] = useState('');
    const [exportSelected, setExportSelected] = useState(new Set());
    const [exportBusy, setExportBusy] = useState(false);
    const [exportError, setExportError] = useState(null);
    const [exportFilterGroup, setExportFilterGroup] = useState('');
    const [exportFilterType, setExportFilterType] = useState('all');

    useEscapeClose(open && !exportBusy, onClose);

    // Init à l'ouverture : charge org/formateur mémorisés + présélection.
    useEffect(() => {
        if (!open) return;
        setExportError(null);
        setExportFilterType('all');
        setExportFilterGroup('');
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get('/custom/thematique-settings.php', { params: { user_thematique_id: thematiqueId } });
                if (!cancelled) {
                    setExportOrg(data.data?.organisation_name || '');
                    setExportTrainer(data.data?.trainer_name || '');
                }
            } catch { /* settings indispo → champs vides */ }
            if (cancelled) return;
            if (initialSelectedIds) {
                setExportSelected(new Set(Array.from(initialSelectedIds)));
            } else {
                const ids = new Set();
                categoryCodes.forEach(cc => (cc.children || []).forEach(ch => { if (ch.status === 'FINISHED') ids.add(ch.id); }));
                quizCodes.forEach(qc => { if (qc.status === 'FINISHED') ids.add(qc.id); });
                codes.forEach(sc => { if (sc.status === 'REDEEMED') ids.add('sc:' + sc.id); });
                setExportSelected(ids);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const toggleExportId = (id) => {
        setExportSelected(prev => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    };

    const generateExportPdf = async () => {
        if (exportSelected.size === 0) return;
        setExportBusy(true);
        setExportError(null);
        try {
            // Mémorise org/formateur sur la thématique (pré-remplissage la prochaine fois)
            await api.post('/custom/thematique-settings.php', {
                user_thematique_id: thematiqueId,
                organisation_name: exportOrg,
                trainer_name: exportTrainer,
            });
            const selected = Array.from(exportSelected);
            const quizIds  = selected.filter(x => typeof x === 'number').join(',');
            const shareIds = selected.filter(x => typeof x === 'string' && x.startsWith('sc:')).map(x => x.slice(3)).join(',');
            const res = await api.get('/code-export.php', {
                params: { user_thematique_id: thematiqueId, quiz_code_ids: quizIds, share_code_ids: shareIds },
                responseType: 'text',
                transformResponse: [(d) => d],
            });
            const blob = new Blob([res.data], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            onClose();
        } catch (e) {
            setExportError(e.response?.data?.message || e.message || 'Erreur');
        }
        setExportBusy(false);
    };

    if (!open) return null;

    // Seuls les codes terminés/utilisés sont exportables (attestation).
    const allRows = [];
    categoryCodes.forEach(cc => (cc.children || []).forEach(ch => {
        if (ch.status !== 'FINISHED') return;
        allRows.push({
            id: ch.id,
            type: 'category',
            participant: cc.participant_name || cc.participant_email || cc.email_invitation || '—',
            sujet: (cc.category_label || '') + ' → ' + (ch.sous_sujet_label || ''),
            group: cc.label || '',
            score: ch.score, total: ch.total,
        });
    }));
    quizCodes.forEach(qc => {
        if (qc.status !== 'FINISHED') return;
        allRows.push({
            id: qc.id,
            type: 'mono',
            participant: qc.participant_name || qc.participant_email || qc.email_invitation || '—',
            sujet: qc.sous_sujet_label || '',
            group: qc.label || '',
            score: qc.score, total: qc.total,
        });
    });
    const statsByEmailExport = new Map((userStats || []).map(u => [(u.email || '').toLowerCase(), u]));
    codes.forEach(sc => {
        if (sc.status !== 'REDEEMED') return;
        const stat = sc.redeemed_by_email ? statsByEmailExport.get(sc.redeemed_by_email.toLowerCase()) : null;
        allRows.push({
            id: 'sc:' + sc.id,
            type: 'thematique',
            participant: sc.redeemed_by_name || sc.redeemed_by_email || '—',
            sujet: `Thématique — accès complet`,
            group: sc.label || '',
            score: stat ? stat.correct_answers : null,
            total: stat ? stat.total_answers : null,
            quizCount: stat ? stat.quiz_completed : 0,
            successRate: stat ? stat.success_rate : null,
            code: sc.code,
        });
    });
    const uniqueGroups = Array.from(new Set(allRows.map(r => r.group).filter(Boolean))).sort();
    const rows = allRows.filter(r =>
        (exportFilterType === 'all' || r.type === exportFilterType) &&
        (exportFilterGroup === '' || r.group === exportFilterGroup)
    );
    const categoryRows   = rows.filter(r => r.type === 'category');
    const monoRows       = rows.filter(r => r.type === 'mono');
    const thematiqueRows = rows.filter(r => r.type === 'thematique');

    return (
        <div className="sem-overlay" onClick={() => !exportBusy && onClose()}>
            <div className="sem-modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
                <div className="sem-header">
                    <BarChart3 size={16} />
                    <span className="sem-title">Exporter les résultats (PDF Qualiopi)</span>
                    <button className="sem-close" onClick={() => !exportBusy && onClose()}><X size={18} /></button>
                </div>
                <div className="sem-section" style={{ padding: '14px 18px' }}>
                    <p style={{ fontSize: '0.86em', color: 'var(--text-muted)', marginTop: 0 }}>
                        Génère une attestation imprimable (ou à enregistrer en PDF via Ctrl+P) des résultats des participants sélectionnés — utilisable comme preuve d'évaluation Qualiopi.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                        <label style={{ fontSize: '0.82em' }}>
                            Organisme de formation
                            <input type="text" value={exportOrg} onChange={e => setExportOrg(e.target.value)}
                                placeholder="Ex: Prépa FPT Lyon"
                                style={{ display: 'block', width: '100%', padding: '6px 10px', marginTop: 3, borderRadius: 6, border: '1px solid var(--border)' }} />
                        </label>
                        <label style={{ fontSize: '0.82em' }}>
                            Nom du formateur
                            <input type="text" value={exportTrainer} onChange={e => setExportTrainer(e.target.value)}
                                placeholder="Ex: Jean Dupont"
                                style={{ display: 'block', width: '100%', padding: '6px 10px', marginTop: 3, borderRadius: 6, border: '1px solid var(--border)' }} />
                        </label>
                    </div>
                    <p style={{ fontSize: '0.75em', color: 'var(--text-muted)', margin: '-6px 0 10px' }}>
                        Ces champs seront mémorisés pour les prochains exports de cette thématique.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 10, fontSize: '0.82em' }}>
                        <label>
                            Type
                            <select value={exportFilterType} onChange={e => setExportFilterType(e.target.value)}
                                style={{ display: 'block', width: '100%', padding: '5px 8px', marginTop: 3, borderRadius: 6, border: '1px solid var(--border)' }}>
                                <option value="all">Tous ({allRows.length})</option>
                                <option value="thematique">Codes thématique ({allRows.filter(r => r.type === 'thematique').length})</option>
                                <option value="category">Codes catégorie ({allRows.filter(r => r.type === 'category').length})</option>
                                <option value="mono">Codes mono-sujet ({allRows.filter(r => r.type === 'mono').length})</option>
                            </select>
                        </label>
                        <label>
                            Groupe
                            <select value={exportFilterGroup} onChange={e => setExportFilterGroup(e.target.value)}
                                style={{ display: 'block', width: '100%', padding: '5px 8px', marginTop: 3, borderRadius: 6, border: '1px solid var(--border)' }}>
                                <option value="">Tous les groupes</option>
                                {uniqueGroups.map(g => (
                                    <option key={g} value={g}>{g} ({allRows.filter(r => r.group === g).length})</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', fontSize: '0.85em' }}>
                        <button className="btn btn-xs btn-ghost" onClick={() => setExportSelected(new Set(rows.map(r => r.id)))}>Cocher le filtre</button>
                        <button className="btn btn-xs btn-ghost" onClick={() => { const n = new Set(exportSelected); rows.forEach(r => n.delete(r.id)); setExportSelected(n); }}>Décocher le filtre</button>
                        <button className="btn btn-xs btn-ghost" onClick={() => setExportSelected(new Set())}>Tout décocher</button>
                        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>{exportSelected.size} sélectionné{exportSelected.size > 1 ? 's' : ''} · {rows.length} affiché{rows.length > 1 ? 's' : ''}</span>
                    </div>

                    {(() => {
                        const visibleIds = new Set(rows.map(r => r.id));
                        const hiddenSelected = Array.from(exportSelected).filter(id => !visibleIds.has(id)).length;
                        if (hiddenSelected === 0) return null;
                        return (
                            <div style={{ padding: '6px 10px', marginBottom: 8, background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.4)', borderRadius: 6, fontSize: '0.82em', color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <AlertTriangle size={13} />
                                <span><strong>{hiddenSelected}</strong> code{hiddenSelected > 1 ? 's sont sélectionnés' : ' est sélectionné'} mais masqué{hiddenSelected > 1 ? 's' : ''} par les filtres actuels. <strong>L'export inclura toute la sélection</strong>, pas seulement ce qui est affiché.</span>
                                <button className="btn btn-xs btn-ghost" style={{ marginLeft: 'auto' }}
                                    onClick={() => { const n = new Set(exportSelected); Array.from(n).forEach(id => { if (!visibleIds.has(id)) n.delete(id); }); setExportSelected(n); }}>
                                    Ne garder que le filtre
                                </button>
                            </div>
                        );
                    })()}

                    <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
                        {rows.length === 0 && (
                            <div style={{ padding: 14, fontSize: '0.88em', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                {allRows.length === 0 ? 'Aucun code terminé pour le moment — l\'export est réservé aux sessions finalisées.' : 'Aucun code ne correspond à ces filtres.'}
                            </div>
                        )}
                        {thematiqueRows.length > 0 && (
                            <div>
                                <div style={{ padding: '5px 10px', background: 'var(--bg-subtle, #f8fafc)', borderBottom: '1px solid var(--border)', fontSize: '0.78em', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.3, textTransform: 'uppercase' }}>
                                    Codes thématique ({thematiqueRows.length})
                                </div>
                                {thematiqueRows.map(r => (
                                    <label key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid var(--border)', fontSize: '0.85em', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={exportSelected.has(r.id)} onChange={() => toggleExportId(r.id)} />
                                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            <strong>{r.participant}</strong>
                                            <span style={{ color: 'var(--text-muted)' }}> · {r.sujet}</span>
                                            {r.group && <span style={{ marginLeft: 6, fontSize: '0.82em', padding: '1px 6px', borderRadius: 3, background: 'rgba(124,58,237,0.1)', color: 'var(--primary)' }}>{r.group}</span>}
                                        </span>
                                        <span style={{ minWidth: 110, textAlign: 'right', fontSize: '0.82em', color: 'var(--heading)' }}>
                                            {r.quizCount} quiz{r.successRate !== null && <> · {r.successRate}%</>}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                        {categoryRows.length > 0 && (() => {
                            const groups = new Map();
                            categoryCodes.forEach(cc => {
                                const visibleChildren = (cc.children || []).filter(ch => categoryRows.some(r => r.id === ch.id));
                                if (visibleChildren.length === 0) return;
                                const finishedScore = visibleChildren.filter(c => c.status === 'FINISHED').reduce((acc, c) => ({ s: acc.s + (c.score || 0), t: acc.t + (c.total || 0), n: acc.n + 1 }), { s: 0, t: 0, n: 0 });
                                groups.set(cc.id, {
                                    participant: cc.participant_name || cc.participant_email || cc.email_invitation || '—',
                                    group: cc.label,
                                    code: cc.code,
                                    category: cc.category_label,
                                    children: visibleChildren,
                                    agg: finishedScore,
                                });
                            });
                            return (
                                <div>
                                    <div style={{ padding: '5px 10px', background: 'var(--bg-subtle, #f8fafc)', borderBottom: '1px solid var(--border)', fontSize: '0.78em', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.3, textTransform: 'uppercase' }}>
                                        Codes catégorie ({groups.size} participant{groups.size > 1 ? 's' : ''} · {categoryRows.length} sujet{categoryRows.length > 1 ? 's' : ''})
                                    </div>
                                    {Array.from(groups.values()).map(g => {
                                        const allChecked = g.children.every(ch => exportSelected.has(ch.id));
                                        const someChecked = g.children.some(ch => exportSelected.has(ch.id));
                                        return (
                                            <div key={g.code} style={{ borderBottom: '2px solid var(--border)' }}>
                                                <label style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '7px 10px', background: 'rgba(124,58,237,0.05)', fontSize: '0.85em', cursor: 'pointer' }}>
                                                    <input type="checkbox"
                                                        ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                                                        checked={allChecked}
                                                        onChange={() => {
                                                            setExportSelected(prev => {
                                                                const n = new Set(prev);
                                                                if (allChecked) g.children.forEach(ch => n.delete(ch.id));
                                                                else g.children.forEach(ch => n.add(ch.id));
                                                                return n;
                                                            });
                                                        }} />
                                                    <span style={{ flex: 1, minWidth: 0 }}>
                                                        <strong>{g.participant}</strong>
                                                        <span style={{ color: 'var(--text-muted)' }}> · {g.category}</span>
                                                        {g.group && <span style={{ marginLeft: 6, fontSize: '0.82em', padding: '1px 6px', borderRadius: 3, background: 'rgba(124,58,237,0.1)', color: 'var(--primary)' }}>{g.group}</span>}
                                                    </span>
                                                    <span style={{ fontSize: '0.82em', color: 'var(--text-muted)' }}>
                                                        {g.agg.n}/{g.children.length} terminé{g.children.length > 1 ? 's' : ''}
                                                        {g.agg.t > 0 && ` · ${g.agg.s}/${g.agg.t}`}
                                                    </span>
                                                </label>
                                                {g.children.map(ch => (
                                                    <label key={ch.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 10px 5px 32px', borderBottom: '1px solid var(--border)', fontSize: '0.82em', cursor: 'pointer' }}>
                                                        <input type="checkbox" checked={exportSelected.has(ch.id)} onChange={() => toggleExportId(ch.id)} />
                                                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                                                            → {ch.sous_sujet_label}
                                                        </span>
                                                        <span style={{ minWidth: 60, textAlign: 'right', fontSize: '0.82em', color: 'var(--heading)' }}>
                                                            {ch.score}/{ch.total}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}

                        {monoRows.length > 0 && (
                            <div>
                                <div style={{ padding: '5px 10px', background: 'var(--bg-subtle, #f8fafc)', borderBottom: '1px solid var(--border)', fontSize: '0.78em', fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.3, textTransform: 'uppercase' }}>
                                    Codes mono-sujet ({monoRows.length})
                                </div>
                                {monoRows.map(r => (
                                    <label key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid var(--border)', fontSize: '0.85em', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={exportSelected.has(r.id)} onChange={() => toggleExportId(r.id)} />
                                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            <strong>{r.participant}</strong>
                                            <span style={{ color: 'var(--text-muted)' }}> · {r.sujet}</span>
                                            {r.group && <span style={{ marginLeft: 6, fontSize: '0.82em', padding: '1px 6px', borderRadius: 3, background: 'rgba(124,58,237,0.1)', color: 'var(--primary)' }}>{r.group}</span>}
                                        </span>
                                        <span style={{ minWidth: 60, textAlign: 'right', fontSize: '0.82em', color: 'var(--heading)' }}>
                                            {r.score}/{r.total}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {exportError && (
                        <div className="sem-notice sem-notice--error" style={{ marginTop: 10 }}>{exportError}</div>
                    )}

                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                        <button className="btn btn-secondary" onClick={onClose} disabled={exportBusy}>Annuler</button>
                        <button className="btn btn-primary" onClick={generateExportPdf} disabled={exportBusy || exportSelected.size === 0}>
                            {exportBusy ? <><Loader2 size={14} className="spin" /> Génération…</> : <>Générer l'attestation PDF</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
